/**
 * Hybrid presence heartbeat. A ping is sent only when nothing has reached the
 * server for `idleMs`, so an actively saving participant adds no extra load.
 * One checker timer (not one per event). Failures are ignored on purpose.
 */

export interface HeartbeatDeps {
  now: () => number
  send: (hidden: boolean) => Promise<unknown>
  isHidden: () => boolean
  /** Return false while offline or after submit to skip pings. */
  isActive: () => boolean
  setInterval: (fn: () => void, ms: number) => unknown
  clearInterval: (handle: unknown) => void
  /** Quiet period before a ping is due. Default 15 s. */
  idleMs?: number
  /** How often the single checker runs. Default 5 s. */
  checkMs?: number
}

export interface Heartbeat {
  start: () => void
  stop: () => void
  /** Record a successful contact with the server (save, tick, time, ...). */
  touch: () => void
  /** Run one check now; exposed for tests. */
  check: () => void
}

export const HEARTBEAT_IDLE_MS = 15_000
export const HEARTBEAT_CHECK_MS = 5_000

export function createHeartbeat(deps: HeartbeatDeps): Heartbeat {
  const idleMs = deps.idleMs ?? HEARTBEAT_IDLE_MS
  const checkMs = deps.checkMs ?? HEARTBEAT_CHECK_MS
  let lastContactAt = deps.now()
  let handle: unknown = null
  let inFlight = false
  let stopped = true

  const touch = () => {
    lastContactAt = deps.now()
  }

  const check = () => {
    if (stopped || inFlight || !deps.isActive()) return
    if (deps.now() - lastContactAt < idleMs) return
    inFlight = true
    let pending: Promise<unknown>
    try {
      pending = deps.send(deps.isHidden())
    } catch {
      inFlight = false
      return
    }
    pending
      .then(() => {
        if (!stopped) touch()
      })
      .catch(() => {
        // Silent by design: the next check retries; no banner, no retry burst.
      })
      .finally(() => {
        inFlight = false
      })
  }

  return {
    start() {
      if (!stopped) return
      stopped = false
      lastContactAt = deps.now()
      handle = deps.setInterval(check, checkMs)
    },
    stop() {
      stopped = true
      if (handle !== null) deps.clearInterval(handle)
      handle = null
    },
    touch,
    check,
  }
}
